import React, { useState } from 'react';
import { 
  Upload, 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  FileText,
  Table
} from 'lucide-react';

export default function ImportWizard({ onClose, onComplete, initialData = null }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [autoDownloading, setAutoDownloading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    file: null,
    fileName: '',
    fileType: '',
    fileContent: '',
    fileSize: 0
  });

  const [parseResult, setParseResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Auto-download and load file if Quick Import was used
  React.useEffect(() => {
    if (initialData?.downloadUrl) {
      autoLoadFile(initialData.downloadUrl, initialData.type);
    }
  }, []);

  const autoLoadFile = async (url, fileType) => {
    setAutoDownloading(true);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const fileName = url.split('/').pop();
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = fileType === 'xlsx' ? 
          btoa(String.fromCharCode(...new Uint8Array(event.target.result))) :
          event.target.result;

        setFormData(prev => ({
          ...prev,
          fileName,
          fileType: fileType === 'xlsx' ? 'excel' : fileType,
          fileContent: content,
          fileSize: blob.size
        }));

        // Auto-parse file
        await parseFile(content, fileType === 'xlsx' ? 'excel' : fileType, fileName);
      };

      if (fileType === 'xlsx') {
        reader.readAsArrayBuffer(blob);
      } else {
        reader.readAsText(blob);
      }
    } catch (error) {
      console.error('Error auto-loading file:', error);
      alert('Failed to load test data file. Please upload manually.');
    } finally {
      setAutoDownloading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileType = file.name.endsWith('.csv') ? 'csv' : 
                     file.name.endsWith('.xlsx') || file.name.endsWith('.xls') ? 'excel' : 
                     file.name.endsWith('.json') ? 'json' : null;

    if (!fileType) {
      alert('Unsupported file type. Please upload CSV, Excel, or JSON.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = fileType === 'excel' ? 
        btoa(String.fromCharCode(...new Uint8Array(event.target.result))) :
        event.target.result;

      setFormData({
        ...formData,
        file,
        fileName: file.name,
        fileType,
        fileContent: content,
        fileSize: file.size
      });

      // Auto-parse file
      await parseFile(content, fileType, file.name);
    };

    if (fileType === 'excel') {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const parseFile = async (content, type, fileName) => {
    setLoading(true);
    try {
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'parse-file',
          data: {
            fileContent: content,
            fileType: type,
            fileName
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setParseResult(result);
        setStep(2);
      } else {
        alert('Failed to parse file: ' + result.error);
      }
    } catch (error) {
      alert('Error parsing file: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateSchema = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate-schema',
          data: {
            columns: parseResult.columns,
            sampleData: parseResult.sampleData
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setValidationResult(result);
        setStep(3);
      }
    } catch (error) {
      alert('Error validating schema: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startImport = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId') || 'temp-user-id';

      // Re-parse file to get full data
      const parseResponse = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'parse-file',
          data: {
            fileContent: formData.fileContent,
            fileType: formData.fileType,
            fileName: formData.fileName
          }
        })
      });

      const fullParseResult = await parseResponse.json();

      // Create import
      const response = await fetch('/api/data-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-import',
          data: {
            userId,
            name: formData.name,
            description: formData.description,
            sourceType: formData.fileType,
            fileName: formData.fileName,
            fileSize: formData.fileSize,
            parsedData: fullParseResult.sampleData, // TODO: Use full data
            columnMappings: validationResult.mappings
          }
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setImportResult(result);
        setStep(4);
      } else {
        alert('Import failed: ' + result.error);
      }
    } catch (error) {
      alert('Error importing data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/20 rounded-lg p-2">
              <Upload className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Import Dataset</h2>
              <p className="text-sm text-slate-400">Step {step} of 4</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-blue-500 text-white' :
                  'bg-slate-700 text-slate-400'
                }`}>
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    s < step ? 'bg-green-500' : 'bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>Upload</span>
            <span>Validate</span>
            <span>Configure</span>
            <span>Import</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Upload File */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Upload Data File</h3>
              
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-12 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".csv,.xlsx,.xls,.json"
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="bg-slate-700/50 rounded-full p-6">
                      <Upload className="w-12 h-12 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Drop file here or click to browse</p>
                      <p className="text-sm text-slate-400">Supported: CSV, Excel (.xlsx, .xls), JSON</p>
                      <p className="text-xs text-slate-500 mt-2">Max size: 50MB</p>
                    </div>
                  </div>
                </label>
              </div>

              {formData.fileName && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div className="flex-1">
                      <p className="text-green-400 font-medium">{formData.fileName}</p>
                      <p className="text-sm text-green-300">{(formData.fileSize / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center space-x-2 text-blue-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Parsing file...</span>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Validate Schema */}
          {step === 2 && parseResult && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Schema Validation</h3>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">File parsed successfully</span>
                </div>
                <p className="text-sm text-green-300">
                  Found {parseResult.rowCount} rows and {parseResult.columns.length} columns
                </p>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Detected Columns:</h4>
                <div className="flex flex-wrap gap-2">
                  {parseResult.columns.map((col, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Sample Data (first 3 rows):</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-600">
                        {parseResult.columns.slice(0, 5).map((col, idx) => (
                          <th key={idx} className="text-left text-slate-400 p-2">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parseResult.sampleData.slice(0, 3).map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-700">
                          {parseResult.columns.slice(0, 5).map((col, colIdx) => (
                            <td key={colIdx} className="text-slate-300 p-2">{row[col]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Configure Import */}
          {step === 3 && validationResult && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Configure Import</h3>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Dataset Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., School A Student Data"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this dataset"
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-slate-700/30 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Column Mappings:</h4>
                <div className="space-y-2">
                  {validationResult.mappings.map((mapping, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                      <span className="text-slate-300">{mapping.sourceColumn}</span>
                      <ArrowRight className="w-4 h-4 text-slate-500" />
                      <span className="text-white font-medium">{mapping.targetColumn}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        mapping.confidence === 1.0 ? 'bg-green-500/20 text-green-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {Math.round(mapping.confidence * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {validationResult.missingRequired.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-red-400 font-medium">Missing Required Fields:</p>
                      <p className="text-sm text-red-300 mt-1">
                        {validationResult.missingRequired.join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Import Results */}
          {step === 4 && importResult && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Import Complete</h3>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-green-400 mb-2">Import Successful!</h4>
                <p className="text-green-300">
                  {importResult.validRecords} of {importResult.totalRecords} records imported
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400 mb-1">Total Records</p>
                  <p className="text-2xl font-bold text-white">{importResult.totalRecords}</p>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400 mb-1">Valid</p>
                  <p className="text-2xl font-bold text-green-400">{importResult.validRecords}</p>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-400 mb-1">Invalid</p>
                  <p className="text-2xl font-bold text-red-400">{importResult.invalidRecords}</p>
                </div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-400 font-medium mb-2">Validation Errors (first 10):</p>
                  <div className="space-y-1 text-sm text-yellow-300">
                    {importResult.errors.map((err, idx) => (
                      <div key={idx}>Row {err.row}: {err.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 p-6 flex items-center justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{step === 1 ? 'Cancel' : 'Back'}</span>
          </button>

          {step < 3 && step === 2 && (
            <button
              onClick={validateSchema}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Validating...</span>
                </>
              ) : (
                <>
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}

          {step === 3 && (
            <button
              onClick={startImport}
              disabled={!formData.name || loading}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Start Import</span>
                </>
              )}
            </button>
          )}

          {step === 4 && (
            <button
              onClick={() => {
                onComplete(importResult);
                onClose();
              }}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <Check className="w-4 h-4" />
              <span>Done</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
